import type { MouseEvent, SyntheticEvent } from 'react'
import { legacyContractDataAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchSubsDisclosure } from '@/msgdeskRuntime/subsDisclosure'
import { createPagingModel, dispatchSubsNavigation, type PagingTarget } from '@/msgdeskRuntime/subsNavigation'
import { dispatchSubsSelection } from '@/msgdeskRuntime/subsSelection'
import { actionContextForDeskMode, dispatchCheckedItemAction, itemActionsForContext, type ItemActionContext, type ItemActionId } from '@/msgdeskRuntime/itemMovements'
import { selectCheckedSubIds, selectFavsAllowed } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'

function stop(event: SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

export function CardsFoldSubsButton({
  section = 0,
  label,
  count,
  kind = 'subs',
}: {
  section?: 0 | 1
  label: string
  count?: number
  kind?: 'subs' | 'msg'
}) {
  const isMsg = kind === 'msg'
  const contract: LegacyContractName = isMsg
    ? 'main.foldButton'
    : section === 0 ? 'subs.menuToggle.answers' : 'subs.menuToggle.submsges'

  function handleFold(event: MouseEvent<HTMLElement>) {
    stop(event)
    if (isMsg) dispatchSubsDisclosure({ action: 'toggle-main' })
    else dispatchSubsDisclosure({ action: 'toggle-section', section, target: event.currentTarget })
  }

  return (
    <a
      href="#"
      {...legacyHookAttrs(contract, 'md-fold-subs md-fold-subs--cards inline-flex w-max items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 no-underline hover:bg-violet-100 hover:text-violet-800')}
      onClick={handleFold}
    >
      <span>{label}</span>
      {!isMsg && typeof count === 'number' ? <span className="text-slate-400">({count})</span> : null}
    </a>
  )
}

export function CardsPagingBar({
  section,
  currentPage = 1,
  totalPages = 1,
  registerContract = true,
}: {
  section: 0 | 1
  currentPage?: number
  totalPages?: number
  registerContract?: boolean
}) {
  const contract = section === 0 ? 'subs.paging.answers' : 'subs.paging.submsges'
  const paging = createPagingModel(currentPage, totalPages)
  const pageClass = 'md-paging__page inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-xs font-semibold text-slate-600 hover:bg-white hover:text-violet-700'

  function pageClick(event: MouseEvent<HTMLElement>, page: PagingTarget) {
    stop(event)
    if (page === 'previous' && paging.previousDisabled) return
    if (page === 'next' && paging.nextDisabled) return
    dispatchSubsNavigation({ action: 'set-page', section, page, target: event.currentTarget })
  }

  return (
    <div
      {...(registerContract ? legacyHookAttrs(contract, 'md-paging md-paging--cards inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1') : { className: 'md-paging md-paging--cards inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1' })}
    >
      {paging.tokens.map((token, index) => token === 'gap' ? (
        <span key={`gap-${index}`} className="px-1 text-slate-400">…</span>
      ) : (
        <a
          key={token}
          href="#"
          className={`${pageClass}${token === paging.currentPage ? ' md-paging__page--current bg-violet-600 text-white hover:bg-violet-600 hover:text-white' : ''}`}
          data-md-legacy-class={token === paging.currentPage ? 'mcss-current_page-cssm' : 'mcss-page_el-cssm'}
          onClick={event => pageClick(event, token)}
        >
          {token}
        </a>
      ))}
      {/* Disabled edge buttons stay MOUNTED but display:none — the legacy
          prev/next click router resolves prev vs next by index inside this
          sequence span, so unmounting Prev would misroute Next as "prev". */}
      <span className="md-paging__sequence inline-flex items-center gap-1">
        <a href="#" className={`${pageClass}${paging.previousDisabled ? ' md-hook-disabled opacity-40' : ''}`} style={paging.previousDisabled ? { display: 'none' } : undefined} data-md-legacy-class={paging.previousDisabled ? 'mcss-str_disable-cssm' : 'mcss-page_el-cssm'} onClick={event => pageClick(event, 'previous')} aria-label="Previous page">‹</a>
        <a href="#" className={`${pageClass}${paging.nextDisabled ? ' md-hook-disabled opacity-40' : ''}`} style={paging.nextDisabled ? { display: 'none' } : undefined} data-md-legacy-class={paging.nextDisabled ? 'mcss-str_disable-cssm' : 'mcss-page_el-cssm'} onClick={event => pageClick(event, 'next')} aria-label="Next page">›</a>
      </span>
    </div>
  )
}

export function CardsRefreshSubs({ section }: { section: 0 | 1 }) {
  return (
    <button
      type="button"
      className="md-refresh-subs md-refresh-subs--cards inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-base text-slate-600 hover:bg-violet-100 hover:text-violet-700"
      title="Refresh"
      onClick={event => dispatchSubsNavigation({ action: 'refresh', section, target: event.currentTarget })}
    >
      ↻
    </button>
  )
}

type CheckedsMode = 'msg' | 'trash' | 'drafts' | 'favs'

const actionLabels: Readonly<Record<ItemActionContext, Partial<Record<ItemActionId, string>>>> = {
  active: { 'move-to-trash': 'Delete', 'move-to-drafts': 'In Drafts', 'add-favorite': 'Favorite' },
  drafts: { 'move-to-trash': 'Delete', 'restore-to-active': 'Create' },
  trash: { 'restore-to-active': 'Restore' },
  favorites: { 'remove-favorite': 'Unfavorite' },
}


export function CardsCheckedsInfo({
  section,
  mode = 'msg',
}: {
  section: 0 | 1
  mode?: CheckedsMode
  visual?: 'modern' | 'legacy'
}) {
  const { state } = useSubsStore()
  const context = actionContextForDeskMode(mode)
  // FAVS-IDENTITY: bulk favorite actions hide with the rest of the favs UI.
  const favsAllowed = selectFavsAllowed(state)
  const sectionKey = section === 0 ? 'answers' : 'submsges'
  const checkedMode = state.renderProjection.mode === 'subs' ? sectionKey : state.renderProjection.mode
  const checkedIds = selectCheckedSubIds(state, sectionKey, checkedMode)
  const checkedSet = new Set(checkedIds)
  const currentIds = state.renderProjection.rootIds[sectionKey] || []
  const currentCount = currentIds.filter(id => checkedSet.has(id)).length
  const contract = section === 0 ? 'subs.checked.answers' : 'subs.checked.submsges'

  function runAction(event: MouseEvent<HTMLElement>, action: ItemActionId) {
    void dispatchCheckedItemAction({ action, context, section, target: event.currentTarget })
  }

  return (
    <div {...legacyContractDataAttrs(contract)} className="md-checked-info md-checked-info--cards inline-flex min-h-8 items-center gap-1.5 text-xs text-slate-600">
      <button type="button" className="h-8 rounded-lg bg-slate-100 px-2 hover:bg-violet-100 hover:text-violet-700" title="Select visible" onClick={event => dispatchSubsSelection({ action: 'toggle-visible', section, target: event.currentTarget })}>
        ✓ {currentCount || ''}{checkedIds.length ? ` (${checkedIds.length})` : ''}
      </button>
      {itemActionsForContext(context).filter(action => favsAllowed || (action !== 'add-favorite' && action !== 'remove-favorite')).map(action => actionLabels[context][action] ? (
        <button key={action} type="button" className="h-8 rounded-lg bg-violet-50 px-2 font-medium text-violet-700 hover:bg-violet-100" data-md-item-action={action} onClick={event => runAction(event, action)}>
          {actionLabels[context][action]}
        </button>
      ) : null)}
    </div>
  )
}
